<!-- src/views/admin/AdminHealthSafetyDashboardView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & GLOBAL COMMAND -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 relative z-30 text-left border-x border-t border-slate-800">
          <div class="absolute top-0 right-0 p-12 opacity-5"><ShieldExclamationIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">H&S Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Registry Intelligence • Real-Time Oversight & Analytics</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <!-- 🛡️ ADMINISTRATIVE COMMANDS: UNRESTRICTED -->
                <router-link to="/admin/manage-health-safety-form-options" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2">
                    <AdjustmentsHorizontalIcon class="h-4 w-4" /> <span>Manage Options</span>
                </router-link>

                <router-link to="/admin/manage-health-safety-form-text" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg">
                    <DocumentTextIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Manage Text</span>
                </router-link>

                <!-- 🛡️ NAVIGATION STANDARDISED: Back button label updated from 'Admin' to 'Return' -->
                <router-link to="/client-admin" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
                  <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10">
              <div class="flex flex-wrap gap-4 items-center">
                <div class="flex flex-col gap-1.5 min-w-[240px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                  <input v-model="filters.search" type="text" placeholder="Reference or Name..." class="filter-input-dark" />
                </div>

                <div class="flex flex-col gap-1.5 min-w-[280px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Form Registry Selection</span>
                  <select v-model="activeTab" class="filter-select-dark">
                    <option v-for="tab in formTabs" :key="tab.id" :value="tab.id">
                      {{ tab.label }} ({{ tabCounts[tab.id] || 0 }})
                    </option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 min-w-[160px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Status Filter</span>
                  <select v-model="filters.status" class="filter-select-dark">
                    <option value="all">All Statuses</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                    <option value="Requires Action">Requires Action</option>
                  </select>
                </div>

                <!-- Dynamic Audit Timeframe Filter -->
                <div class="flex flex-col gap-1.5 min-w-[160px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Timeframe Filter</span>
                  <select v-model="filters.timeframe" class="filter-select-dark">
                    <option value="all">All Time</option>
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="this_month">This Month</option>
                  </select>
                </div>
              </div>

              <button 
                @click="exportToExcel"
                :disabled="exporting || filteredSubmissions.length === 0"
                class="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-white/20 transition-all flex items-center gap-3 disabled:opacity-50 shadow-xl"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 🛡️ 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS HUB -->
        <div class="mb-12 animate-fadeIn relative z-10 text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative text-left">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4 text-left">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2">Active Multi-Form Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left">
                 <div v-for="stat in safetyStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current registry submissions for patterns and risks.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Hotspot Trends</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Last 50 Records
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Form Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 4. DATA DISPLAY -->
        <div class="text-center py-24" v-if="loading">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Hydrating H&S Registry...</p>
        </div>

        <div class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner animate-fadeIn" v-else-if="filteredSubmissions.length === 0">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30 mx-auto mb-4" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight text-center">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 text-center">No records identified for the selected protocol.</p>
        </div>

        <div class="space-y-4 animate-fadeIn text-left" v-else>
          <div v-for="sub in paginatedSubmissions" :key="sub.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group">
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
              
              <!-- REFERENCE -->
              <div class="flex items-center gap-6 w-full xl:w-[22%] shrink-0">
                <div class="relative h-20 w-20 shrink-0 bg-white p-1 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                  <img :src="sub.qrCodeUrl" alt="QR" class="w-full h-full object-contain" />
                </div>
                <div class="min-w-0 text-left">
                  <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">
                    {{ sub.humanReadableId || sub.referenceNumber || 'REF-TBC' }}
                  </h3>
                </div>
              </div>

              <!-- SUBMITTER -->
              <div class="w-full xl:w-[23%] px-4 border-l border-slate-50 min-w-0 flex flex-col items-start gap-1 text-left">
                <h4 class="text-sm font-black text-[#0A1630] uppercase leading-none truncate w-full">
                   {{ getDisplayName(sub) }}
                </h4>
                <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap mt-1">
                   {{ formatDate(getVal(sub, 'submittedAt')) }}
                </span>
              </div>

              <!-- DETAIL -->
              <div class="w-full xl:w-[25%] px-4 border-l border-slate-50 min-w-0 flex flex-col items-start text-left">
                <template v-if="sub.formIdentifier === 'observationForm'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Observation Type</p>
                    <p class="text-xl font-black text-[#20FCF1] uppercase tracking-tight leading-tight">
                      {{ getObservationLabel(getVal(sub, 'observationType')) }}
                    </p>
                </template>
                <template v-else-if="sub.formIdentifier === 'worksiteInspectionForm'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Site Access</p>
                    <p class="text-[11px] font-black text-[#0A1630] uppercase tracking-tight leading-tight truncate w-full">
                      {{ getVal(sub, 'siteAccess') || 'Unspecified Site' }}
                    </p>
                </template>
                <template v-else-if="sub.formIdentifier === 'accidentForm'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Event Category</p>
                    <p class="text-[11px] font-black text-rose-500 uppercase tracking-tight leading-tight">
                      {{ getVal(sub, 'category') || 'Incident' }}
                    </p>
                </template>
                <template v-else-if="sub.formIdentifier === 'cossCommunicationsMonitoring'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Competence Standard Achieved</p>
                    <span 
                      :class="getCompetenceColorClass(getVal(sub, 'competenceStandard') || getVal(sub, 'competenceStandardAchieved') || getVal(sub, 'overallRating') || getVal(sub, 'rating'))"
                      class="px-3 py-1 text-[10px] font-black rounded-full uppercase tracking-tight mt-1 inline-block border"
                    >
                      {{ getCompetenceFullLabel(getVal(sub, 'competenceStandard') || getVal(sub, 'competenceStandardAchieved') || getVal(sub, 'overallRating') || getVal(sub, 'rating')) || 'N/A' }}
                    </span>
                </template>
                <template v-else-if="sub.formIdentifier === 'clientFeedbackForm'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Client Rating & Project</p>
                    <div class="flex items-center gap-2 mt-1">
                      <span class="text-xs font-black text-[#0A1630] uppercase">{{ getVal(sub, 'projectName') || 'Project Closeout' }}</span>
                      <span class="flex items-center gap-1 bg-amber-50 px-2.5 py-0.5 rounded-md border border-amber-100 text-[10px] font-black text-amber-700">
                        <StarIcon class="w-3 h-3 fill-amber-400 text-amber-400" /> {{ getVal(sub, 'overallRating') || '5.0' }}
                      </span>
                    </div>
                </template>
                <template v-else-if="sub.formIdentifier === 'singleItemInspectionForm'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Asset & Site</p>
                    <p class="text-xs font-black text-[#0A1630] uppercase">{{ sub.itemDetails?.assetId || 'ASSET' }} | {{ sub.itemDetails?.siteLocation || 'Site' }}</p>
                </template>
                <template v-else-if="sub.formIdentifier === 'multiItemInspectionForm'">
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Shift & Location</p>
                    <p class="text-xs font-black text-[#0A1630] uppercase">{{ sub.siteDetails?.siteLocation || 'Shift' }}</p>
                </template>
                <template v-else>
                    <p class="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1 leading-none">Audit Detail</p>
                    <p class="text-[11px] font-bold text-slate-500 uppercase tracking-tight truncate w-full italic">
                      Compliance Data Sealed
                    </p>
                </template>
              </div>

              <!-- STATUS -->
              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6 border-l-0 xl:border-l xl:border-slate-50 xl:pl-6">
                 <div class="text-right shrink-0">
                    <span
                      :class="statusClass(getVal(sub, 'status'))"
                      class="px-5 py-2 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm"
                    >
                      {{ getVal(sub, 'status') || 'PENDING' }}
                    </span>
                 </div>

                 <router-link 
                  :to="{ name: 'completedForm', params: { collectionName: getCollectionName(sub.formIdentifier), submissionId: sub.id }}" 
                  class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 min-w-[180px]"
                >
                  <EyeIcon class="h-4 w-4" /> <span>Review Full Audit</span>
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
          <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
            <div class="flex items-center gap-4">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                &larr; Prev
              </button>
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
                Page {{ currentPage }} of {{ totalPages || 1 }}
              </span>
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages || totalPages === 0"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                Next &rarr;
              </button>
            </div>
            
            <!-- Symmetrical Neon-Chevron Items Per Page Selector (Forced h-11 height) -->
            <div class="flex items-center gap-3">
              <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
              <select 
                v-model="itemsPerPage" 
                class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
                style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
              >
                <option :value="25">25 Rows</option>
                <option :value="50">50 Rows</option>
                <option :value="100">100 Rows</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
// eslint-disable-next-line no-unused-vars
import QRCode from 'qrcode';
import { 
  ShieldExclamationIcon, ArrowUturnLeftIcon,
  AdjustmentsHorizontalIcon, EyeIcon, TableCellsIcon, MagnifyingGlassIcon,
  ExclamationTriangleIcon, ChatBubbleLeftRightIcon, ClipboardDocumentCheckIcon,
  SignalIcon, ShieldCheckIcon, DocumentTextIcon,
  MagnifyingGlassCircleIcon, SparklesIcon, CpuChipIcon, StarIcon, WrenchScrewdriverIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const functions = getFunctions(auth.app, 'europe-west2');

const loading = ref(true);
const exporting = ref(false);
const allSubmissions = ref([]); 
const analyticsData = ref({});

// PAGINATION STATE
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ STICKY FILTERS CONFIGURATION [1.1.4]
const activeTab = ref(localStorage.getItem('hs_sticky_tab') || 'observationForm');
const tabCounts = reactive({});
const observationOptions = ref([]);

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

const filters = reactive({
  search: localStorage.getItem('hs_sticky_search') || '',
  status: localStorage.getItem('hs_sticky_status') || 'all',
  timeframe: localStorage.getItem('hs_sticky_timeframe') || 'all'
});

// 🛡️ STICKY WATCHERS: Dynamically persist selections [1.1.4]
watch(() => activeTab.value, (val) => localStorage.setItem('hs_sticky_tab', val));
watch(() => filters.search, (val) => localStorage.setItem('hs_sticky_search', val));
watch(() => filters.status, (val) => localStorage.setItem('hs_sticky_status', val));
watch(() => filters.timeframe, (val) => localStorage.setItem('hs_sticky_timeframe', val));

const formTabs = [
  { id: 'observationForm', label: 'Observations', icon: EyeIcon },
  { id: 'accidentForm', label: 'Accidents', icon: ExclamationTriangleIcon },
  { id: 'accidentWitnessStatementForm', label: 'Witness Statements', icon: ChatBubbleLeftRightIcon },
  { id: 'hsInvestigationRegistry', label: 'Investigations', icon: MagnifyingGlassCircleIcon },
  { id: 'worksiteInspectionForm', label: 'Inspections', icon: ClipboardDocumentCheckIcon },
  { id: 'cossCommunicationsMonitoring', label: 'COSS Monitoring', icon: SignalIcon },
  { id: 'clientFeedbackForm', label: 'Client Feedback', icon: StarIcon },
  { id: 'singleItemInspectionForm', label: 'Single-Item Plant Inspections', icon: WrenchScrewdriverIcon },
  { id: 'multiItemInspectionForm', label: 'Multi-Item Shift Checks', icon: TableCellsIcon },
  { id: 'ppeRequest', label: 'PPE Requests', icon: ShieldCheckIcon }
];

const getCollectionName = (formId) => formId === 'ppeRequest' ? 'onboardingFormSubmissions' : 'healthsafetyFormSubmissions';
const getVal = (sub, key) => sub?.formData?.[key] ?? sub?.[key] ?? null;

const getObservationLabel = (val) => {
  if (!val) return 'Unspecified';
  const match = observationOptions.value.find(o => o.value === val);
  return match ? match.text : val.replace(/_/g, ' ').toUpperCase();
};

const getDisplayName = (sub) => {
  if (sub.formIdentifier === 'cossCommunicationsMonitoring') {
    const candidate = getVal(sub, 'candidateName') || getVal(sub, 'candidate') || getVal(sub, 'candidateDisplayName') || getVal(sub, 'candidateCredentials')?.name;
    if (candidate) return candidate;
  }
  if (sub.formIdentifier === 'clientFeedbackForm') {
    return getVal(sub, 'clientName') || getVal(sub, 'clientRepresentative') || 'Client Representative';
  }
  if (sub.formIdentifier === 'singleItemInspectionForm') {
    return sub.signOff?.inspectorName || sub.userName || 'Inspector';
  }
  if (sub.formIdentifier === 'multiItemInspectionForm') {
    return sub.signOff?.inspectorName || sub.userName || 'Inspector';
  }
  return getVal(sub, 'userName') || 'Guest';
};

const getCompetenceFullLabel = (val) => {
  if (!val) return '';
  const normalized = val.toString().trim().toUpperCase();
  if (normalized.startsWith('A')) return 'A - Fully Competent';
  if (normalized.startsWith('B')) return 'B - Competent with Development';
  if (normalized.startsWith('C')) return 'C - Requires Action';
  if (normalized.startsWith('D')) return 'D - Requires Immediate Action';
  return val;
};

const getCompetenceColorClass = (val) => {
  if (!val) return 'bg-slate-50 text-slate-500 border-slate-200';
  const normalized = val.toString().trim().toUpperCase();
  if (normalized.startsWith('A')) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (normalized.startsWith('B')) return 'bg-yellow-50 text-yellow-850 border border-yellow-200';
  if (normalized.startsWith('C')) return 'bg-[#FFF7ED] text-[#EA580C] border border-[#FFEDD5]';
  if (normalized.startsWith('D')) return 'bg-rose-50 text-rose-700 border border-rose-200';
  return 'bg-slate-50 text-slate-500 border-slate-200';
};

const statusClass = (status) => {
  const s = (status || 'PENDING').toUpperCase();
  if (s === 'COMPLETED' || s === 'REVIEWED') return 'bg-[#EBFDF8] text-[#0A1630] border border-[#20FCF1]/30';
  if (s === 'REQUIRES ACTION') return 'bg-red-50 text-red-700 border border-red-200';
  return 'bg-amber-50 text-amber-700 border border-amber-200';
};

const safetyStats = computed(() => {
  const hsData = analyticsData.value?.healthAndSafety || {};
  return [
    { label: 'Total Registry', value: allSubmissions.value.length, color: '#20FCF1', icon: ClipboardDocumentCheckIcon },
    { label: 'Open Incidents', value: hsData.totalAccidents || 0, color: '#EF4444', icon: ExclamationTriangleIcon },
    { label: 'Active Observations', value: hsData.totalObservations || 0, color: '#FBBF24', icon: EyeIcon },
    { label: 'Client Feedback', value: allSubmissions.value.filter(s => s.formIdentifier === 'clientFeedbackForm').length, color: '#10B981', icon: StarIcon },
  ];
});

const filteredSubmissions = computed(() => {
  return allSubmissions.value.filter(s => {
    const matchTab = s.formIdentifier === activeTab.value;
    const matchSearch = !filters.search || 
      (getDisplayName(s) || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (s.humanReadableId || '').toLowerCase().includes(filters.search.toLowerCase()) ||
      (getVal(s, 'projectName') || '').toLowerCase().includes(filters.search.toLowerCase());
    
    const statusVal = (getVal(s, 'status') || '').toLowerCase();
    const matchStatus = filters.status === 'all' || 
                        statusVal === filters.status.toLowerCase() ||
                        (filters.status === 'Completed' && statusVal === 'reviewed');
    
    let matchTimeframe = true;
    if (filters.timeframe !== 'all') {
        const submittedAtRaw = getVal(s, 'submittedAt');
        if (!submittedAtRaw) {
            matchTimeframe = false;
        } else {
            const recordDate = submittedAtRaw.toDate ? submittedAtRaw.toDate() : new Date(submittedAtRaw);
            const now = new Date();
            const diffMs = now - recordDate;
            
            if (filters.timeframe === '24h') {
                matchTimeframe = diffMs <= 24 * 60 * 60 * 1000;
            } else if (filters.timeframe === '7d') {
                matchTimeframe = diffMs <= 7 * 24 * 60 * 60 * 1000;
            } else if (filters.timeframe === '30d') {
                matchTimeframe = diffMs <= 30 * 24 * 60 * 60 * 1000;
            } else if (filters.timeframe === 'this_month') {
                matchTimeframe = recordDate.getMonth() === now.getMonth() && recordDate.getFullYear() === now.getFullYear();
            }
        }
    }
    
    return matchTab && matchSearch && matchStatus && matchTimeframe;
  });
});

const totalPages = computed(() => {
  return Math.ceil(filteredSubmissions.value.length / itemsPerPage.value);
});

const paginatedSubmissions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredSubmissions.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = userStore.clientId;
    if (!cid) { alert("Identity context missing."); return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const last50 = allSubmissions.value.slice(0, 50);
        const accidents = last50.filter(f => f.formIdentifier === 'accidentForm');
        const observations = last50.filter(f => f.formIdentifier === 'observationForm');
        const investigations = last50.filter(f => f.formIdentifier === 'hsInvestigationRegistry');
        const feedback = last50.filter(f => f.formIdentifier === 'clientFeedbackForm');

        const categories = last50.map(f => getVal(f, 'observationCategory') || getVal(f, 'category')).filter(Boolean);
        const topCategory = categories.length ? categories.sort((a,b) => categories.filter(v => v===a).length - categories.filter(v => v===b).length).pop() : 'General Conduct';
        
        const report = {
            summary: `Audit of last 50 registry entries identifies a ${accidents.length > 2 ? 'elevated' : 'stable'} incident profile. ${observations.length} active observations and ${feedback.length} client feedback questionnaires are currently archived for annual auditing. Primary safety focal point: ${topCategory}.`,
            trends: [
                { label: 'Incident Density', value: `${accidents.length} Records found`, isCritical: accidents.length > 3 },
                { label: 'Client Feedback Index', value: `${feedback.length} Reviews logged`, isCritical: false },
                { label: 'Hotspot Sector', value: topCategory, isCritical: true }
            ],
            actions: [
                { type: 'Safety Alert', instruction: `Conduct briefing on ${topCategory}`, reason: 'Recurring category identified in current registry cycle.' },
                { type: 'Admin Action', instruction: `Review ${investigations.length} investigations`, reason: 'Stage 2 appraisal required for closure.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'safety_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        isAiLoading.value = false;
    }
};

const fetchHSFormOptions = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'formOptions', 'observationTypes'));
    if (docSnap.exists()) {
      observationOptions.value = docSnap.data()[cid] || docSnap.data().global || [];
    }
  } catch (err) { console.error("H&S Options Registry Failure:", err); }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'safety_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Intelligence Registry Neutral.");
  }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const cid = userStore.claims?.clientId || userStore.userProfile?.clientId;
    if (!cid) return;

    await Promise.all([
      fetchHSFormOptions(cid),
      fetchLatestAiReport(cid)
    ]);

    const [hsSnap, onbSnap] = await Promise.all([
      getDocs(query(collection(db, 'healthsafetyFormSubmissions'), where('clientId', '==', cid))),
      getDocs(query(collection(db, 'onboardingFormSubmissions'), where('clientId', '==', cid), where('formIdentifier', '==', 'ppeRequest')))
    ]);

    const rawDocs = [
      ...hsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      ...onbSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    ];

    const profile = userStore.userProfile;
    const myDepts = (profile?.departments || []).map(d => d.toLowerCase().trim());
    if (profile?.department) myDepts.push(profile.department.toLowerCase().trim());
    const uniqueMyDepts = [...new Set(myDepts)];
    
    const isSuperAdmin = userStore.role === 'super-admin';
    const isFullAdmin = userStore.role === 'admin';
    const canViewAllHS = isSuperAdmin || isFullAdmin || userStore.permissions?.canViewHealthAndSafety === true;

    const filteredDocs = rawDocs.filter(sub => {
      if (canViewAllHS) return true;
      const subDept = (sub.formData?.department || sub.department || '').toLowerCase().trim();
      return uniqueMyDepts.includes(subDept);
    });

    const processedDocs = await Promise.all(filteredDocs.map(async (docData) => {
      let resolvedName = docData.userName || 'Guest';
      const submitterId = docData.userId || docData.submittedBy;
      if (submitterId) {
        try {
          const pSnap = await getDoc(doc(db, 'userProfiles', submitterId));
          if (pSnap.exists()) {
            resolvedName = `${pSnap.data().firstName || ''} ${pSnap.data().lastName || ''}`.trim() || 'Guest';
          }
        } catch (err) {
          console.warn("Name resolution masked.");
        }
      }
      
      const url = `${window.location.origin}/completed-form/${getCollectionName(docData.formIdentifier)}/${docData.id}`;
      let qrUrl = null;
      try { 
        qrUrl = await QRCode.toDataURL(url, { width: 200, margin: 1 }); 
      } catch (e) { 
        console.error("QR Code Generation Error:", e); 
      }
      
      return { ...docData, userName: resolvedName, qrCodeUrl: qrUrl };
    }));

    allSubmissions.value = processedDocs.sort((a, b) => {
      const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
      const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
      return dateB - dateA;
    });

    formTabs.forEach(tab => tabCounts[tab.id] = allSubmissions.value.filter(d => d.formIdentifier === tab.id).length);
    const result = await httpsCallable(functions, 'getAnalyticsData')();
    analyticsData.value = result.data;
  } catch (err) { 
    console.error("H&S Registry Sync Error:", err); 
  } finally { 
    loading.value = false; 
  }
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  const date = val.toDate ? val.toDate() : new Date(val);
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatHeader = (key) => key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const exportToExcel = () => {
  exporting.value = true;
  try {
    const dataToExport = filteredSubmissions.value.map(sub => {
      const row = { 
        'Reference': sub.humanReadableId || sub.referenceNumber || 'N/A', 
        'Personnel': getDisplayName(sub), 
        'Status': getVal(sub, 'status') || 'PENDING', 
        'Submitted': formatDate(getVal(sub, 'submittedAt')) 
      };

      const formData = sub.formData || {};
      Object.keys(formData).forEach(k => { 
        if (!['signature', 'photo', 'url', 'base64'].some(s => k.toLowerCase().includes(s))) { 
          row[formatHeader(k)] = formData[k]; 
        } 
      });

      const appraisal = sub.appraisalStage2 || {};
      row['Management Corrective & Preventive Actions'] = appraisal.notes || '';
      row['Management Lessons Learned'] = appraisal.lessonsLearned || '';
      row['Management Additional Comments'] = appraisal.recommendations || '';
      row['Reviewed By'] = appraisal.appraisedBy || 'Unreviewed';
      row['Reviewed At'] = appraisal.appraisedAt ? formatDate(appraisal.appraisedAt) : '';

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Safety_Registry");
    XLSX.writeFile(wb, `Sentinel_Safety_Export_${activeTab.value}.xlsx`);
  } catch (err) { 
    console.error("Excel Generation Error:", err);
    alert("Export failed: " + err.message); 
  } finally { 
    exporting.value = false; 
  }
};

onMounted(() => { if (userStore.isAuthReady) fetchData(); });

watch([activeTab, () => filters.search, () => filters.status, () => filters.timeframe, itemsPerPage], () => {
  currentPage.value = 1;
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 1rem center; background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>